import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  UnstyledButton,
  Page,
  Popover,
  Text,
  Link,
  Banner,
  Layout,
  SkeletonBodyText,
  BlockStack,
  Card,
  Icon,
  Box,
  OptionList,
  TextField,
  Form,
} from '@shopify/polaris';
import {usePerformanceMark, Stage} from '@shopify/react-performance';
import {useI18n} from '@shopify/react-i18n';
import {InfoIcon, ChevronDownIcon, SearchIcon} from '@shopify/polaris-icons';
import {useToggle} from '@shopify/react-hooks';
import {useMachineTranslate} from 'utilities/use-machine-translate';
import {LoadingBar} from 'components/LoadingBar';
import {useUrl} from '@web-utilities/url';
import {usePreview} from 'utilities/use-preview';
import {useShop} from '@web-utilities/shop';
import {useLocalStorage} from '@web-utilities/local-storage';
import {
  LOCALE_STORAGE_KEY,
  MT_UNSUPPORTED_RTL_LOCALES,
  THEME_ID_STORAGE_KEY,
} from 'features/Translations/constants';
import {
  ShopLocale,
  useShopLocalesAndMarkets,
} from 'utilities/use-shop-locales-and-markets';
import {useLocation} from 'react-router-dom';
import {getQueryFromSearch} from 'utilities/url';
import {useUrlParams} from 'utilities/use-url-params';

import {RTL_WARNING_BANNER_DISMISSED_STORAGE_KEY} from './constants';
import {
  StatusCard,
  GetStartedCard,
  GroupedResourceList,
  SkeletonHome,
  MachineTranslationsReadyBanner,
  TranslationsSyncCard,
} from './components';
import styles from './Home.scss';

const I18N_SCOPE = 'Home';
const LANGUAGE_FILTER_THRESHOLD = 8;

export default function Home() {
  usePerformanceMark(Stage.Complete, 'Home');
  const [i18n] = useI18n();
  const urlHelper = useUrl();
  const {shopEmail} = useShop();

  const [{update: storeSelectedEditorLocale}] =
    useLocalStorage<ShopLocale['locale']>(LOCALE_STORAGE_KEY);
  const [
    {value: rtlWarningBannerDismissed, update: storeRtlWarningBannerDismissed},
  ] = useLocalStorage<Boolean>(RTL_WARNING_BANNER_DISMISSED_STORAGE_KEY);

  const [languageFilterValue, setLanguageFilter] = useState('');
  const [{remove: removeStoredThemeId, update: updateStoredThemeId}] =
    useLocalStorage<string>(THEME_ID_STORAGE_KEY);
  const {
    loading: localesAndMarketsLoading,
    locales: shopLocales,
    markets,
    currentLocale: currentShopLocale,
  } = useShopLocalesAndMarkets();
  const {
    value: languageListVisible,
    toggle: toggleLanguageList,
    setFalse: hideLanguageList,
  } = useToggle(false);
  const {
    loading: previewLoading,
    canPreview,
    preview,
    previewForm,
  } = usePreview();
  const {setUrlParams} = useUrlParams();

  const {
    enabled: machineTranslationEnabled,
    initialized: machineTranslationInitialized,
    ConfirmMachineTranslateModal,
    confirmMachineTranslate,
    inProgressBatch: machineTranslationBatchInProgress,
    lastBatch: lastMachineTranslationBatch,
  } = useMachineTranslate({
    resourceName: currentShopLocale?.name || '',
  });

  const machineTranslationInProgress = Boolean(
    machineTranslationBatchInProgress,
  );
  const prevMtInProgress = useRef<boolean>(false);
  const [mtHasCompleted, setMtHasCompleted] = useState(false);
  useEffect(() => {
    if (prevMtInProgress.current && !machineTranslationInProgress) {
      setMtHasCompleted(true);
    }

    prevMtInProgress.current = machineTranslationInProgress;
  }, [machineTranslationInProgress]);

  const {search} = useLocation();
  const query = getQueryFromSearch(search);
  const themeIdFromURL = query.themeId;
  useEffect(() => {
    if (themeIdFromURL) {
      updateStoredThemeId(themeIdFromURL);
    } else {
      removeStoredThemeId();
    }
  }, [updateStoredThemeId, removeStoredThemeId, themeIdFromURL]);

  const autoTranslateActionMarkup = useMemo(
    () =>
      !currentShopLocale?.primary &&
      currentShopLocale?.machineTranslatable &&
      (!machineTranslationInitialized || machineTranslationEnabled)
        ? {
            content: i18n.translate('MachineTranslation.autoTranslate'),
            onAction: confirmMachineTranslate,
            disabled:
              !machineTranslationInitialized || machineTranslationInProgress,
            primary: true,
          }
        : null,
    [
      confirmMachineTranslate,
      currentShopLocale?.machineTranslatable,
      currentShopLocale?.primary,
      i18n,
      machineTranslationInitialized,
      machineTranslationEnabled,
      machineTranslationInProgress,
    ],
  );

  const machineTranslationInprogressText = useMemo(() => {
    const machineTranslationLanguage = shopLocales.find(
      ({locale}) => locale === machineTranslationBatchInProgress?.locale,
    );

    if (
      shopEmail &&
      machineTranslationLanguage &&
      machineTranslationLanguage !== currentShopLocale
    ) {
      return i18n.translate(
        'MachineTranslation.inProgress.wholeStoreWithLanguage',
        {
          language: machineTranslationLanguage.name,
          email: (
            <Text variant="bodyMd" fontWeight="bold" as="span">
              {shopEmail}
            </Text>
          ),
        },
      );
    }

    return i18n.translate('MachineTranslation.inProgress.wholeStore', {
      email: (
        <Text variant="bodyMd" fontWeight="bold" as="span">
          {shopEmail}
        </Text>
      ),
    });
  }, [
    shopLocales,
    currentShopLocale,
    i18n,
    shopEmail,
    machineTranslationBatchInProgress?.locale,
  ]);

  const machineTranslationInProgressBanner = useMemo(
    () => (
      <LoadingBar
        loading={machineTranslationInProgress}
        text={machineTranslationInprogressText}
      />
    ),
    [machineTranslationInProgress, machineTranslationInprogressText],
  );

  const notMachineTranslatableBanner = useMemo(
    () =>
      currentShopLocale?.machineTranslatable ? undefined : (
        <div className={styles.NotMachineTranslatableBanner}>
          <Banner
            tone="info"
            icon={InfoIcon}
            title={i18n.translate('Home.notMachineTranslatableBanner.title', {
              language: currentShopLocale?.name || '',
            })}
          >
            {i18n.translate('Home.notMachineTranslatableBanner.description', {
              docLink: (
                <Link
                  target="_blank"
                  url={urlHelper.helpCenterUrl({
                    locale: i18n.locale,
                    path: 'manual/markets/languages/translate',
                  })}
                >
                  {i18n.translate(
                    'Home.notMachineTranslatableBanner.docLinkLabel',
                  )}
                </Link>
              ),
            })}
          </Banner>
        </div>
      ),
    [
      currentShopLocale?.machineTranslatable,
      currentShopLocale?.name,
      i18n,
      urlHelper,
    ],
  );

  const machineTranslationsLastBatchDate =
    machineTranslationEnabled && lastMachineTranslationBatch?.createdAt
      ? new Date(lastMachineTranslationBatch.createdAt)
      : undefined;
  const storeReadyInLanguageBanner = useMemo(
    () =>
      currentShopLocale &&
      machineTranslationEnabled &&
      lastMachineTranslationBatch &&
      mtHasCompleted &&
      !machineTranslationInProgress ? (
        <MachineTranslationsReadyBanner
          locale={currentShopLocale}
          preview={preview}
        />
      ) : null,
    [
      currentShopLocale,
      machineTranslationEnabled,
      lastMachineTranslationBatch,
      mtHasCompleted,
      machineTranslationInProgress,
      preview,
    ],
  );

  const rtlWarningBanner = useMemo(() => {
    if (
      rtlWarningBannerDismissed ||
      (currentShopLocale &&
        !MT_UNSUPPORTED_RTL_LOCALES.includes(currentShopLocale.locale))
    ) {
      return null;
    }

    return (
      <Banner
        tone="warning"
        onDismiss={() => storeRtlWarningBannerDismissed(true)}
      >
        {i18n.translate('Home.rtlWarningBanner.content', {
          link: (
            <Link
              url={urlHelper.helpCenterUrl({
                locale: i18n.locale,
                path: 'manual/markets/languages',
              })}
              target="_blank"
            >
              {i18n.translate('Home.rtlWarningBanner.link')}
            </Link>
          ),
        })}
      </Banner>
    );
  }, [
    currentShopLocale,
    i18n,
    rtlWarningBannerDismissed,
    storeRtlWarningBannerDismissed,
    urlHelper,
  ]);

  const languagesList = useMemo(() => {
    const lowerCaseSearch = languageFilterValue.toLocaleLowerCase();
    return shopLocales
      .filter(
        (locale) =>
          locale.name.toLowerCase().includes(lowerCaseSearch) ||
          locale.locale.toLowerCase().includes(lowerCaseSearch),
      )
      .map((shopLocale) => ({
        active: currentShopLocale?.locale === shopLocale.locale,
        label: shopLocale.name,
        value: shopLocale.locale,
      }));
  }, [currentShopLocale?.locale, shopLocales, languageFilterValue]);

  const onLanguageChange = useCallback(
    (selected: string) => {
      const shopLocale = shopLocales.find(({locale}) => locale === selected);
      if (!shopLocale) return;

      prevMtInProgress.current = false;
      setMtHasCompleted(false);
      setUrlParams({shopLocale: shopLocale.locale});
      if (!shopLocale.primary) {
        storeSelectedEditorLocale(shopLocale.locale);
      }
      hideLanguageList();
      setLanguageFilter('');
    },
    [hideLanguageList, shopLocales, setUrlParams, storeSelectedEditorLocale],
  );

  const languageFilter = useMemo(
    () =>
      shopLocales.length >= LANGUAGE_FILTER_THRESHOLD ? (
        <Box padding="200" paddingBlockEnd="0">
          <div className={styles.LanguageFilter}>
            <Form onSubmit={() => onLanguageChange(languagesList[0].value)}>
              <TextField
                placeholder={i18n.translate('Home.languageSearchPlaceholder')}
                label={i18n.translate('Home.languageSearchPlaceholder')}
                labelHidden
                autoComplete="off"
                onChange={setLanguageFilter}
                value={languageFilterValue}
                clearButton
                onClearButtonClick={() => setLanguageFilter('')}
                prefix={<Icon source={SearchIcon} />}
                type="search"
              />
            </Form>
          </div>
        </Box>
      ) : null,
    [
      i18n,
      languageFilterValue,
      shopLocales.length,
      languagesList,
      onLanguageChange,
    ],
  );

  const languageListActivator = useMemo(
    () => (
      <UnstyledButton
        className={styles.PageTitlePickerActivatorButton}
        onClick={toggleLanguageList}
      >
        {currentShopLocale?.name}
        <Icon source={ChevronDownIcon} tone="base" />
      </UnstyledButton>
    ),
    [currentShopLocale, toggleLanguageList],
  );

  const languageSelector = useMemo(
    () =>
      shopLocales.length > 1 ? (
        <Popover
          active={languageListVisible}
          activator={languageListActivator}
          onClose={hideLanguageList}
          preferredAlignment="left"
        >
          {languageFilter}
          <OptionList
            options={languagesList}
            onChange={([selected]) => onLanguageChange(selected)}
            allowMultiple={false}
            selected={
              currentShopLocale?.locale ? [currentShopLocale?.locale] : []
            }
          />
        </Popover>
      ) : (
        <div className={styles.NoDropdownLanguage}>
          {currentShopLocale?.name}
        </div>
      ),
    [
      currentShopLocale,
      hideLanguageList,
      languageListActivator,
      languageListVisible,
      languagesList,
      shopLocales.length,
      onLanguageChange,
      languageFilter,
    ],
  );

  if (localesAndMarketsLoading) {
    return <SkeletonHome />;
  }

  const secondaryActions = [
    {
      content: i18n.translate('Common.viewStore'),
      disabled: previewLoading || !canPreview,
      onAction: preview,
    },
  ];

  return (
    <Page
      title={i18n.translate('title', {scope: [I18N_SCOPE]})}
      titleMetadata={languageSelector}
      primaryAction={autoTranslateActionMarkup}
      secondaryActions={secondaryActions}
    >
      <Box paddingBlockEnd="800">
        <Layout>
          <Layout.Section>
            <BlockStack gap="300">
              {notMachineTranslatableBanner}
              {machineTranslationInProgressBanner}
              {storeReadyInLanguageBanner}
              {rtlWarningBanner}
              <GroupedResourceList
                machineTranslationsActive={Boolean(
                  lastMachineTranslationBatch ||
                    machineTranslationBatchInProgress,
                )}
              />
            </BlockStack>
          </Layout.Section>
          <Layout.Section variant="oneThird">
            <BlockStack gap="300">
              {currentShopLocale &&
              !localesAndMarketsLoading &&
              machineTranslationInitialized ? (
                <>
                  <StatusCard
                    currentLocale={currentShopLocale}
                    markets={markets}
                    machineTranslationsLastBatchDate={
                      machineTranslationsLastBatchDate
                    }
                  />
                  <TranslationsSyncCard currentLocale={currentShopLocale} />
                  <GetStartedCard />
                </>
              ) : (
                <>
                  <Card roundedAbove="sm">
                    <SkeletonBodyText lines={6} />
                  </Card>
                  <Card roundedAbove="sm">
                    <SkeletonBodyText lines={2} />
                  </Card>
                  <Card roundedAbove="sm">
                    <SkeletonBodyText lines={6} />
                  </Card>
                </>
              )}
            </BlockStack>
          </Layout.Section>
          <ConfirmMachineTranslateModal />
          {previewForm}
        </Layout>
      </Box>
    </Page>
  );
}
